import { clsx } from "clsx";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";

import { LoadingSpinner } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { type ISelectionModel, isSelected } from "../../app-utils/selection-model.js";
import { type ParentType, type User } from "../../graphql.js";
import {
  loadFullParentTypeWithAttributes,
  loadParentTypes,
  onParentTypeSelectionChange,
} from "../../redux/modules/sample-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import ParentTypeItem from "./ParentTypeItem.js";
import ParentTypesAdminToolbar from "./ParentTypesAdminToolbar.js";

interface IParentTypesAdminPageOwnProps {}

interface IParentTypesAdminPageAppStateProps {
  // Redux injected props
  parentTypes: ParentType[];
  selectionModel: ISelectionModel;
  user: User;
}

interface IParentTypesAdminPageDispatchProps {
  loadParentTypes: () => Promise<ParentType[]>;
  onSelectionChange: (parentType: ParentType, selected: boolean) => void;
  fetchFullParentTypeWithAttributes: (parentTypeId: number) => Promise<ParentType>;
}

export interface IParentTypesAdminPageProps
  extends IParentTypesAdminPageAppStateProps,
    IParentTypesAdminPageDispatchProps,
    IParentTypesAdminPageOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IParentTypesAdminPageOwnProps,
): IParentTypesAdminPageAppStateProps {
  return {
    parentTypes: state.sampleAdmin.parentTypes,
    selectionModel: state.sampleAdmin.parentTypesSelectionModel,
    user: state.auth.session,
  };
}

function mapDispatchToProps(dispatch): IParentTypesAdminPageDispatchProps {
  return {
    loadParentTypes: () => dispatch(loadParentTypes()),
    onSelectionChange: (parentType: ParentType, selected: boolean) =>
      dispatch(onParentTypeSelectionChange(parentType, selected)),
    fetchFullParentTypeWithAttributes: (parentTypeId: number) =>
      dispatch(loadFullParentTypeWithAttributes(parentTypeId)),
  };
}

interface ISampleTypesAdminPageState {
  isLoadingParentTypes: boolean;
}

class ParentTypesAdminPage extends PureComponent<IParentTypesAdminPageProps, ISampleTypesAdminPageState> {
  constructor(props: IParentTypesAdminPageProps) {
    super(props);

    this.state = {
      isLoadingParentTypes: false,
    };
  }

  loadParentTypes = () => {
    this.setState({ isLoadingParentTypes: true });
    this.props.loadParentTypes().then(() => this.setState({ isLoadingParentTypes: false }));
  };

  componentDidMount() {
    this.loadParentTypes();
  }

  render(): ReactElement {
    return (
      <div className={clsx(styleLayout.page, styleLayout.page_scrollable)} style={{ backgroundColor: "white" }}>
        <PageHeader
          title={"Parent Types"}
          badge={String(this.props.parentTypes.length)}
          actionWidgets={[<ParentTypesAdminToolbar key={"parent-types-admin-page-action-1"} />]}
        />
        {this.state.isLoadingParentTypes ? (
          <LoadingSpinner inCenter />
        ) : (
          <div style={{ height: "calc(100% - 50px)", overflow: "auto" }}>
            <ul className="saved-items">
              {this.props.parentTypes.map((parentType: ParentType) => (
                <ParentTypeItem
                  key={`parent-type-key-${parentType.id}`}
                  parentType={parentType}
                  user={this.props.user}
                  isSelected={isSelected(this.props.selectionModel, parentType.id)}
                  onItemSelectionChange={this.props.onSelectionChange}
                  fetchFullParentTypeWithAttributes={this.props.fetchFullParentTypeWithAttributes}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export default connect<
  IParentTypesAdminPageAppStateProps,
  IParentTypesAdminPageDispatchProps,
  IParentTypesAdminPageOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ParentTypesAdminPage);
